import { useNavigate, useParams } from "react-router-dom";
import { Button, Chip } from "@legal-500/legal500-design-system";
import { NO_EXPERIENCE_VALUE } from "@legal-500/survey-renderer";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { useQuery } from "@/hooks/useApi";
import { useNewAxisEdition } from "@/hooks/useNewAxisEditions";
import { usePublications } from "@/hooks/usePublications";
import { surveysService, type SurveyResponseStatus } from "@/api/surveys.service";
import { ROUTES } from "@/routes";
import { formatDateTime } from "@/utils";
import styles from "./ResponseDetail.module.css";

const STATUS_CHIP_COLOR: Record<SurveyResponseStatus, "success" | "default"> = {
  completed: "success",
  draft: "default",
};

type QuestionMeta = { title: string; noExperienceLabel: string | null };

function pickDefaultText(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (value !== null && typeof value === "object") {
    const text = (value as { default?: unknown }).default;
    return typeof text === "string" && text.trim() ? text.trim() : null;
  }
  return null;
}

function collectElementMeta(elements: unknown[], map: Map<string, QuestionMeta>): void {
  for (const element of elements) {
    if (element === null || typeof element !== "object") continue;
    const el = element as {
      name?: unknown;
      type?: unknown;
      title?: unknown;
      noExperienceLabel?: unknown;
    };
    if (typeof el.name !== "string" || el.type === "html") continue;
    map.set(el.name, {
      title: pickDefaultText(el.title) ?? el.name,
      noExperienceLabel: pickDefaultText(el.noExperienceLabel),
    });
  }
}

function extractQuestionMeta(
  definition: unknown,
  randomQuestions: unknown,
): Map<string, QuestionMeta> {
  const map = new Map<string, QuestionMeta>();
  const pages =
    definition !== null &&
    typeof definition === "object" &&
    Array.isArray((definition as { pages?: unknown }).pages)
      ? (definition as { pages: unknown[] }).pages
      : [];
  for (const page of pages) {
    if (page === null || typeof page !== "object") continue;
    const elements = (page as { elements?: unknown }).elements;
    if (Array.isArray(elements)) collectElementMeta(elements, map);
  }
  if (Array.isArray(randomQuestions)) collectElementMeta(randomQuestions, map);
  return map;
}

type TestimonialEntry = { position: number; subject: string; text: string };

/** Detects the `{ position, subject, text }[]` shape used by multi-testimonial answers. */
function asTestimonialEntries(value: unknown): TestimonialEntry[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const entries: TestimonialEntry[] = [];
  for (const item of value) {
    if (item === null || typeof item !== "object" || Array.isArray(item)) return null;
    const obj = item as Record<string, unknown>;
    if (typeof obj.subject !== "string" && typeof obj.text !== "string") return null;
    entries.push({
      position: typeof obj.position === "number" ? obj.position : entries.length,
      subject: typeof obj.subject === "string" ? obj.subject : "",
      text: typeof obj.text === "string" ? obj.text : "",
    });
  }
  return entries.sort((a, b) => a.position - b.position);
}

function formatAnswerValue(value: unknown, noExperienceLabel: string | null = null): string {
  if (value === null || value === undefined) return "—";
  if (value === NO_EXPERIENCE_VALUE) return noExperienceLabel ?? "No experience";
  const testimonials = asTestimonialEntries(value);
  if (testimonials) {
    return testimonials
      .map(entry => [entry.subject, entry.text].filter(Boolean).join(": "))
      .filter(Boolean)
      .join("\n");
  }
  if (Array.isArray(value)) return value.map(v => String(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function ResponseDetailPage() {
  const navigate = useNavigate();
  const { responseId } = useParams<{ responseId: string }>();

  const { data, loading, error, isRefetching, refetch } = useQuery(
    () => surveysService.getResponseById(responseId!),
    [responseId],
  );

  const response = data?.response;

  const newAxisEditionId = response?.editionId ?? null;

  const { data: publicationsData } = usePublications(
    { edition: response?.edition ?? undefined },
    { enabled: !newAxisEditionId && !!response?.edition },
  );

  const { data: newAxisEdition, error: newAxisEditionError } = useNewAxisEdition(newAxisEditionId);

  const legacyPublicationName =
    response?.publicationId == null
      ? "—"
      : (publicationsData?.publications.find(p => p.id === response.publicationId)?.publication ??
        String(response.publicationId));

  const publicationName = !newAxisEditionId
    ? legacyPublicationName
    : newAxisEditionError
      ? "Could not load publication"
      : (newAxisEdition?.publication ?? "…");

  if (loading && !isRefetching) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Response" />
        <LoadingState message="Loading response..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Response" />
        <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />
      </div>
    );
  }

  if (!response) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Response" />
        <EmptyState
          variant="empty"
          title="Response not found"
          message="This response does not exist or could not be loaded."
        />
      </div>
    );
  }

  const answers = Object.entries(response.responseData ?? {});
  const questionMetaMap = extractQuestionMeta(response.surveyDefinition, response.randomQuestions);

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Survey Response">
        <Button
          variant="outlined"
          color="default"
          onClick={() => navigate(ROUTES.surveys.responses)}
        >
          Back
        </Button>
      </PageHeader>

      <section className={styles.metaSection}>
        <dl className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Firm Ref</dt>
            <dd className={styles.metaValue}>{response.firmRef}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Referee Code</dt>
            <dd className={styles.metaValue}>{response.refereeCode}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Email</dt>
            <dd className={styles.metaValue}>{response.refereeEmail}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Edition</dt>
            <dd className={styles.metaValue}>{response.edition ?? "—"}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Publication</dt>
            <dd className={styles.metaValue}>{publicationName}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Status</dt>
            <dd className={styles.metaValue}>
              <Chip size="small" color={STATUS_CHIP_COLOR[response.status]}>
                {response.status.charAt(0).toUpperCase() + response.status.slice(1)}
              </Chip>
            </dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Locale</dt>
            <dd className={styles.metaValue}>{response.locale?.toUpperCase() ?? "—"}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Completed At</dt>
            <dd className={styles.metaValue}>
              {response.completedAt ? formatDateTime(response.completedAt) : "—"}
            </dd>
          </div>
          <div className={styles.metaItem}>
            <dt className={styles.metaLabel}>Created At</dt>
            <dd className={styles.metaValue}>{formatDateTime(response.createdAt)}</dd>
          </div>
        </dl>
      </section>

      <section className={styles.answersSection}>
        <h2 className={styles.sectionTitle}>Answers</h2>

        {answers.length === 0 ? (
          <p className={styles.emptyAnswers}>No answers recorded.</p>
        ) : (
          <div className={styles.answersList}>
            {answers.map(([questionName, value]) => (
              <div key={questionName} className={styles.answerCard}>
                <dt className={styles.questionLabel}>
                  {questionMetaMap.get(questionName)?.title ?? questionName}
                </dt>
                <dd className={styles.answerValue}>
                  {formatAnswerValue(
                    value,
                    questionMetaMap.get(questionName)?.noExperienceLabel ?? null,
                  )}
                </dd>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default ResponseDetailPage;
